import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "review-the-draft",
  courseId: "getting-started",
  order: 4,
  title: "Review the draft",
  subtitle: "Suggestions, score, placeholders",
  summary:
    "Every draft comes with a score, a list of suggestions, and bracketed placeholders where the AI was missing a fact. Work all three before you export.",
  learn: [
    "Read the report score and what moves it",
    "Resolve suggestions by typing or by recording more audio, then Re-evaluate",
    "Fill in placeholders, and use Hide placeholders to read the draft clean",
    "Clear every placeholder before you export",
  ],
  minutes: 5,
  releasedAt: "2025-06-01T00:00:00.000Z",
  sceneId: "review-the-draft",
  tourId: "review-the-draft",
  body: [
    {
      type: "paragraph",
      text: "After every draft, regeneration, or re-evaluation, the AI scores the report and lists what it is missing: facts it could not establish, sequences that are unclear, elements a charge would need. Those tell you what is left to fix, so you do not have to reread the whole report from scratch.",
    },
    { type: "heading", text: "The score" },
    {
      type: "paragraph",
      text: "The **Suggestions** tab opens with a report score and its label at the top. Labels run from Outstanding and Excellent through Satisfactory, Needs More Detail, Insufficient Information, and Unsatisfactory. Not Evaluated means no score has been produced yet. The score reflects completeness and clarity, not length, and it is a review aid for you, not a supervisor metric.",
    },
    {
      type: "bullets",
      items: [
        "Raises the score: concrete times, named people, a clear sequence, elements of the offense, disposition.",
        "Does not raise the score: length for its own sake, formatting, boilerplate headers and footers.",
      ],
    },
    { type: "heading", text: "Suggestions" },
    {
      type: "paragraph",
      text: "Each suggestion names a gap, such as a missing time, an unnamed witness, or an unclear sequence. Some departments also see policy check and footage check suggestions in the same list. A prefix such as \"[Policy check · Title § n]\" or \"[Footage check\" tells you where a suggestion came from.",
    },
    {
      type: "callout",
      tone: "tip",
      text: "A suggestion with a policy source badge opens the department policy section it came from in the sidebar, so you can read the requirement before you answer it.",
    },
    { type: "heading", text: "Placeholders" },
    {
      type: "paragraph",
      text: "Where the recording never gave a fact, the narrative shows a bracketed placeholder such as [arrival time] instead of a guess. Templates can add their own (\"[Case Number]\"). Click the placeholder and type the real value. Use **Find** in the toolbar to jump through any you missed.",
    },
    {
      type: "callout",
      tone: "note",
      title: "Hide placeholders",
      text: "The **Hide placeholders** toggle in the Suggestions panel collapses placeholder text so you can read the narrative clean. It is remembered in this browser, not on your account, so it will not follow you to another computer. Hidden placeholders are still in the report and still export.",
    },
    { type: "heading", text: "Work the suggestions" },
    {
      type: "steps",
      items: [
        "Open the **Suggestions** tab in the right panel.",
        "Click a suggestion to see what it is asking for.",
        "For a quick fix, click into the narrative and type the detail.",
        "Click **Re-evaluate** to rescore and refresh the list.",
        "For several gaps at once, click **Address Suggestions** and choose **Record Audio** or **Upload Audio**, then answer them out loud. The AI writes your answers into the right places.",
        "Review each change under \"Review Addition\" or \"Review Rewrite\", comparing Original and Rewritten. Keep it, click **Discard**, or click **Discard All**.",
        "Click \"Stop reviewing\" when you are done.",
      ],
    },
    {
      type: "paragraph",
      text: "**Re-evaluate** rereads the narrative, rescores it, archives the old suggestions, and writes a new list.",
    },
    {
      type: "callout",
      tone: "warning",
      title: "Re-evaluate starts over",
      text: "It archives every current suggestion, including policy check items. If you are partway through a list, finish it first.",
    },
    {
      type: "callout",
      tone: "warning",
      title: "Before you export",
      text: "An exported report still contains any placeholder you left in it. Clear them all before you export.",
    },
  ],
  related: [
    "dictate-additions",
    "regenerate-the-narrative",
    "export-your-report",
    "report-preferences-in-depth",
    "policy-check-suggestions",
  ],
};

export default lesson;
